import { type FormInstance, Result, Skeleton } from "antd";
import { FormItems } from "./FormItems";
import { type StrictlyDollars, dollarsFromBasisPoints } from "@vyne-shared/utils/currency";
import { VyneForm } from "@vyne-shared/components/vyneForm/VyneForm";
import { getApiException } from "api/exceptions";
import { useEffect } from "react";
import { useGetProviderSettingsPatient } from "api/clients/useGetProviderSettingsPatient";
import { useMasonFlag } from "utils/useMasonFlag";
import type { ExistingPatient } from "../existingPatient";
import type { PatientUpdateForm } from "api/requests";
import type { ProviderSettingsPatientResponse } from "api/responses";
import type { RequestChannel } from "@vyne-shared/api/VynePayments/responses";

function useIsPmsIntegrated(existingPrp: ExistingPatient) {
    const pmsIntegratedSession = useMasonFlag();

    if (pmsIntegratedSession && !existingPrp) throw new Error("brand new patient unsupported when PMS-integrated");

    return pmsIntegratedSession && (!!existingPrp!.pmsBalances || !!existingPrp!.pmsId);
}

interface DefaultFormValues extends Partial<PatientUpdateForm> {
    requestChannel: RequestChannel;
    amount?: StrictlyDollars;
}

function getDefaultFormValues(
    customization: ProviderSettingsPatientResponse,
    existingPrp: ExistingPatient,
    pmsIntegrated: boolean,
    keepAmount?: boolean,
) {
    const values: DefaultFormValues = {
        allowCommunication: true,
        ...existingPrp,
        requestChannel: existingPrp?.requestChannel ?? customization.defaultChannel,
    };

    // Set amount to remaining PMS balance, unless we explicitly keep a supplied initial value
    if (pmsIntegrated && typeof existingPrp?.pmsBalances?.balanceTotal === "number" && !keepAmount) {
        values.amount = dollarsFromBasisPoints(existingPrp.pmsBalances.balanceEstimated);
    }

    return values;
}

interface PaymentFormProps {
    /** Existing patient details, if any. Will be displayed as default values. */
    readonly existingPrp: ExistingPatient;

    /** The underlying form to store data in. */
    readonly form: FormInstance<PatientUpdateForm>;

    /** Flag to keep the amount specified by {@link existingPrp} instead of pulling from PMS data when available. */
    readonly keepAmount?: boolean;

    /** Flag to disable the Allow Communication checkbox. */
    readonly disableAllowComm?: boolean;

    /** Flag to hide the messaging controls. */
    readonly hideMessaging?: boolean;

    /** Flag to hide the amount input. */
    readonly hideBalance?: boolean;
}

/** Form for creating a new payment. This supports creating new users if necessary. */
export const PaymentForm = ({
    existingPrp,
    form,
    keepAmount,
    disableAllowComm,
    hideMessaging,
    hideBalance,
}: PaymentFormProps) => {
    const { isPending, isError, error, data } = useGetProviderSettingsPatient();

    if (isPending) {
        return <Skeleton active />;
    } else if (isError) {
        const exception = getApiException(
            error,
            "An unknown error has occurred while trying to load configuration settings.",
        );
        return <Result status="error" title="Unable to load settings" subTitle={exception.detail} />;
    } else {
        return (
            <PaymentFormContent
                existingPrp={existingPrp}
                form={form}
                keepAmount={keepAmount}
                disableAllowComm={disableAllowComm}
                hideMessaging={hideMessaging}
                hideBalance={hideBalance}
                data={data}
            />
        );
    }
};

interface PaymentFormContentProps extends PaymentFormProps {
    readonly data: ProviderSettingsPatientResponse;
}

const PaymentFormContent = ({
    data,
    existingPrp,
    keepAmount,
    disableAllowComm,
    hideMessaging,
    form,
    hideBalance,
}: PaymentFormContentProps) => {
    const pmsIntegrated = useIsPmsIntegrated(existingPrp);
    const values = getDefaultFormValues(data, existingPrp, pmsIntegrated, keepAmount);

    // validate on render when there's an existing P/RP provided
    // avoid prematurely marking empty fields as invalid when existing P/RP not provided
    useValidateOnRender(!!existingPrp, form);

    return (
        <VyneForm name="newPaymentRequest" initialValues={values} form={form}>
            <FormItems
                form={form}
                pmsIntegrated={pmsIntegrated}
                existingPrp={existingPrp}
                disableAllowComm={disableAllowComm}
                hideBalance={hideBalance}
                hideMessaging={hideMessaging}
                initialPhone={values.phone}
                initialEmail={values.email}
                initialRequestChannel={values.requestChannel}
            />
        </VyneForm>
    );
};

function useValidateOnRender(validateOnRender: boolean, form: FormInstance) {
    useEffect(() => {
        if (validateOnRender) {
            form.validateFields().catch(() => {
                /* swallow exception. form will display any validation errors. */
            });
        }
    }, [form, validateOnRender]);
}
